import { DATE_FORMAT_FRONT_SHORT, formatFr, parseApiFormat } from '../../../../misc/date.utilities'
import { inputInseeNumberFormatter } from '../../../../misc/inseeNumber.utils'
import { InsiPatient } from '../../../../model/Insi'
import { PatientFormFields } from '../PatientForm.model'
import { ComparisonResultItem } from '../../../../model/ComparisonResult.type'
import { ComparisonStatus } from '../../../../model/ComparisonStatus.enum'
import { capitalize } from 'lodash'
import type { AssigningAuthority, Patient } from '../../../../model/Patient'

const EMPTY_VALUE = ''

export const deserializeInsiPatient = (
  insiPatient: InsiPatient,
  formValues: PatientFormFields,
): PatientFormFields => {
  const formatedBirthdate = insiPatient.birthDate
    ? formatFr(parseApiFormat(insiPatient.birthDate), DATE_FORMAT_FRONT_SHORT)
    : null

  return {
    ...formValues,
    birthFirstName: insiPatient.birthFirstName ?? formValues.birthFirstName,
    birthLastName: insiPatient.birthLastName ?? formValues.birthLastName,
    usedFirstName: insiPatient.usedFirstName ?? formValues.usedFirstName,
    otherFirstNames: insiPatient.otherFirstName ?? formValues.otherFirstNames,
    sex: insiPatient.sex ?? formValues.sex,
    birthDate: formatedBirthdate ?? formValues.birthDate,
    birthPlaceCode: insiPatient.birthPlaceCode ?? formValues.birthPlaceCode,
    assigningAuthority: insiPatient.assigningAuthority ?? formValues.assigningAuthority,
    inseeNumber: insiPatient.inseeNumber
      ? inputInseeNumberFormatter(insiPatient.inseeNumber)
      : formValues.inseeNumber,
    identityStatus: {
      ...formValues.identityStatus,
      insiStatus: {
        checked: 'VALIDATED',
        checkedAt: new Date(),
      },
    },
  }
}
export const deserializeInsiPayload = (insiPatient: InsiPatient): Partial<Patient> => {
  const {
    birthDate,
    birthFirstName,
    birthLastName,
    usedFirstName,
    otherFirstName,
    sex,
    birthPlaceCode,
    assigningAuthority,
    inseeNumber,
  } = insiPatient

  return {
    ...insiPatient,
    birthFirstName: birthFirstName ?? EMPTY_VALUE,
    birthLastName: birthLastName ?? EMPTY_VALUE,
    usedFirstName: usedFirstName ?? EMPTY_VALUE,
    otherFirstNames: otherFirstName ?? EMPTY_VALUE,
    sex: sex ?? EMPTY_VALUE,
    birthDate: birthDate
      ? formatFr(parseApiFormat(birthDate), DATE_FORMAT_FRONT_SHORT)
      : EMPTY_VALUE,
    birthPlaceCode: birthPlaceCode ?? EMPTY_VALUE,
    assigningAuthority: assigningAuthority ?? null,
    inseeNumber: inseeNumber ? inputInseeNumberFormatter(inseeNumber) : EMPTY_VALUE,
  }
}

export const compareOIDValues = (
  insiCompareResult: ComparisonResultItem<
    InsiPatient & { otherFirstNames?: string } & PatientFormFields
  >[],
) => {
  return insiCompareResult.map((item) => {
    if (item.key === 'assigningAuthority') {
      const valueA = (item.valueA as AssigningAuthority)?.oid
      const valueB = (item.valueB as AssigningAuthority)?.oid

      if (valueA === valueB) {
        return { ...item, status: ComparisonStatus.Identical }
      }
    }
    return item
  })
}

export const formatPatientNames = (values: PatientFormFields): PatientFormFields => ({
  ...values,
  birthFirstName: values.birthFirstName?.toLowerCase(),
  usedFirstName: values.usedFirstName?.toLowerCase(),
  otherFirstNames: values.otherFirstNames?.toLowerCase(),
})
export const formatInsiPatientNames = (patient: InsiPatient | null): InsiPatient | null => {
  if (!patient) {
    return null
  }
  return {
    ...patient,
    birthFirstName: patient.birthFirstName?.toLowerCase(),
    usedFirstName: patient.usedFirstName?.toLowerCase() ?? null,
    otherFirstName: patient.otherFirstName?.toLowerCase(),
  }
}

//capitalize first name even if hyphenated or with space between names
export const formatNamesList = (names: string[] | undefined): string[] => {
  if (!names) {
    return []
  }

  return names.map((name) => {
    let capitalized = capitalize(name)

    return capitalized.replace(/([ -])(.)/g, (match, separator, char) => {
      return separator + char.toUpperCase()
    })
  })
}
